// client/src/features/quotations/lineMatchReview.ts
//
// D99 item 4 (docs/plans/rfq-catalogue-matching.md §5): the quotation grid WARNS about a
// catalogue match nobody has looked at, and never blocks. Pure helpers only; the words come
// from `utils/matchReasons` (never re-list codes here).

import { MATCH_DECISION_LABELS, MATCH_REASON_LABELS, needsConfirmation, hasHumanMatch } from '../../utils/matchReasons';
import { formatDateSpelled } from '../../utils/dateFormat';
import { catalogueSearchPickRowName } from './cataloguePickDetails';

export type MatchLine = {
  lineNumber?: number;
  itemName?: string;
  notInCatalogue?: boolean;
  catalogueNearMisses?: number;
  matchDecision?: string;
  matchReasons?: string[];
  matchAcknowledgedBy?: string | null;
  matchAcknowledgedByName?: string | null;
  matchAcknowledgedAt?: string | null;
  matchRememberedBy?: string | null;
  matchRememberedByName?: string | null;
  matchRememberedAt?: string | null;
  pricingDetails?: unknown;
};

const rowIdOf = (d: unknown): string | null => {
  const id = d && typeof d === 'object' ? (d as Record<string, unknown>).masterCatalogueId : null;
  return id == null || id === '' ? null : String(id);
};

/**
 * D157.1: the catalogue row a person matched this line to (`hasHumanMatch`: Confirm, a pick, the
 * remembered match), which the per-line Refresh re-prices instead of searching by name; null otherwise.
 */
export function pinnedCatalogueRowId(line: MatchLine): string | null {
  return hasHumanMatch(line) ? rowIdOf(line.pricingDetails) : null;
}

/** The lookup re-priced the very row this line carries a person's match to (the server's same-row rule). */
const keepsPersonsMatch = (line: MatchLine, lookup: MatchLine): boolean => {
  const row = pinnedCatalogueRowId(line);
  return !!row && row === rowIdOf(lookup.pricingDetails);
};

/** The Refresh's new `pricingDetails`: the lookup's, plus the line's pick mark while a person's row is kept. */
export function pricingDetailsAfterRefresh(
  line: MatchLine,
  lookup: { pricingDetails?: Record<string, unknown> }
): Record<string, unknown> | undefined {
  const mark = keepsPersonsMatch(line, lookup) ? (line.pricingDetails as Record<string, unknown>).catalogueSearchPick : undefined;
  return mark ? { ...lookup.pricingDetails, catalogueSearchPick: mark } : lookup.pricingDetails;
}

/** The per-line Refresh's copy of the lookup's match fields. `lookup` is the lookupItemPrice result. */
export function mergeLookupMatch<T extends MatchLine>(line: T, lookup: MatchLine): T {
  // The lookup has no quotation context: it returns the MACHINE decision, never a person's
  // `confirmed`. So a person's match stands while the line is priced from the same catalogue row
  // (the server carries it by line key and agrees); a different row is a different match and
  // drops it. The server is the authority at save; this keeps the screen honest until then.
  const keepConfirmation = keepsPersonsMatch(line, lookup);
  return {
    ...line,
    notInCatalogue: lookup.notInCatalogue,
    catalogueNearMisses: lookup.catalogueNearMisses,
    ...(keepConfirmation
      ? {}
      : {
          matchDecision: lookup.matchDecision,
          matchReasons: lookup.matchReasons,
          matchAcknowledgedBy: null,
          matchAcknowledgedByName: null,
          matchAcknowledgedAt: null,
          matchRememberedBy: null,
          matchRememberedByName: null,
          matchRememberedAt: null,
        }),
  };
}

/**
 * The catalogue row the line is matched to, as the line itself records it: a pick's row
 * (`catalogueSearchPickRowName`, none for an item still awaiting approval), else what priced it.
 */
export function matchedRowName(line: MatchLine): string | null {
  const pd = line.pricingDetails;
  if (!pd || typeof pd !== 'object') return null;
  const original = (pd as Record<string, unknown>).originalItem;
  return catalogueSearchPickRowName(pd) ?? (typeof original === 'string' && original.trim() ? original.trim() : null);
}

export function reasonLabels(line: MatchLine): string[] {
  return (line.matchReasons || []).map((c) => MATCH_REASON_LABELS[c]).filter(Boolean);
}

/**
 * Tooltip for the name cell of a flagged or confirmed line. The server stamps the confirmer's
 * name on the line (response-only `matchAcknowledgedByName`); "a user" only when it could not load it.
 */
export function matchTooltip(line: MatchLine): string | null {
  const acknowledged = !!line.matchAcknowledgedAt;
  // D99.16: a line the company memory answered says so; a person's own confirmation on top wins.
  const remembered = !acknowledged && (line.matchReasons || []).includes('remembered_match');
  if (!needsConfirmation(line) && !acknowledged && !remembered) return null;
  const parts: string[] = [];
  const decision = MATCH_DECISION_LABELS[line.matchDecision as string];
  if (decision) parts.push(`${decision}.`);
  // The human_* reasons say "a person confirmed/picked this"; the sentence below says who and when.
  const reasons = acknowledged ? (line.matchReasons || []).filter((c) => c !== 'human_confirmed' && c !== 'human_pick') : remembered ? (line.matchReasons || []).filter((c) => c !== 'remembered_match') : line.matchReasons;
  parts.push(...reasonLabels({ matchReasons: reasons }).map((r) => `${r}.`));
  const row = matchedRowName(line);
  if (row) parts.push(`Matched to «${row}».`);
  if (acknowledged) {
    const who = line.matchAcknowledgedByName || 'a user';
    const when = formatDateSpelled(line.matchAcknowledgedAt);
    parts.push(`Confirmed by ${who}${when ? ` on ${when}` : ''}.`);
  }
  if (remembered) {
    const who = line.matchRememberedByName || 'an earlier confirmation';
    const when = formatDateSpelled(line.matchRememberedAt);
    parts.push(`Remembered from ${who}${when ? `, ${when}` : ''}.`);
  }
  return parts.join(' ');
}

/** Lines the send dialog lists: unconfirmed matches and lines not in the catalogue. */
export function linesToReview(lines: MatchLine[] | undefined | null) {
  return (lines || [])
    .filter((l) => needsConfirmation(l) || l.notInCatalogue === true)
    .map((l) => ({
      lineNumber: l.lineNumber as number,
      itemName: l.itemName || '',
      notInCatalogue: l.notInCatalogue === true,
      reasons: needsConfirmation(l)
        ? [MATCH_DECISION_LABELS[l.matchDecision as string], ...reasonLabels(l)].filter(Boolean)
        : ['Not in the Master Catalogue'],
    }));
}
export type ReviewEntry = ReturnType<typeof linesToReview>[number];

/** Plain words for the server's refusals; the client never shows server prose. */
export function confirmRefusalMessage(code?: string): string {
  switch (code) {
    case 'MATCH_NOT_CONFIRMABLE':
      return 'This line has nothing to confirm: its match is not waiting on a person.';
    case 'QUOTATION_FROZEN':
      return 'This quotation is frozen, so its lines can no longer be confirmed.';
    case 'MATCH_LINE_CHANGED':
      return 'This line changed since you opened it. Refresh the quotation and look again.';
    default:
      return 'Could not confirm this match. Please try again.';
  }
}
